import { isObject } from '@scalar/helpers/object/is-object';
import { forEachPathItemOperation, getResolvedPathItem, } from '@scalar/workspace-store/helpers/for-each-path-item-operation';
import { getResolvedRef, mergeSiblingReferences } from '@scalar/workspace-store/helpers/get-resolved-ref';
import remarkGfm from 'remark-gfm';
import remarkStringify from 'remark-stringify';
import { unified } from 'unified';
import { anchor, createDocumentAnchors } from './document-anchors.js';
import { createDocumentExamples } from './document-examples.js';
import { field, heading, inlineCode, item, link, list, paragraph, strong, text } from './markdown-nodes.js';
import { createDescriptionParser, expandDescriptions } from './parse-description.js';
import { renderExamples } from './render-examples.js';
import { formatOperationMethod, renderOperation } from './render-operation.js';
import { createSchemaRenderer } from './render-schema.js';
import { renderSecurity } from './render-security.js';
const serializer = unified()
    .use(remarkGfm)
    .use(remarkStringify, {
    bullet: '-',
    join: [
        (left, right, parent) => {
            if (!('spread' in parent)) {
                return undefined;
            }
            // Inside a tight list item, content after a nested list would otherwise continue its last item.
            if (left.type === 'list' && right.type !== 'list') {
                return 1;
            }
            // Code blocks in descriptions read better apart from the prose around them.
            if (left.type === 'code' || right.type === 'code') {
                return 1;
            }
            return undefined;
        },
    ],
})
    .freeze();
/** Authored values must survive even when the model does not generate an object example. */
const hasAuthoredExamples = (view) => isObject(view.schema) &&
    (view.schema.example !== undefined || (Array.isArray(view.schema.examples) && view.schema.examples.length > 0));
/**
 * Refer to a model that an operation or an earlier model on the same page already expanded.
 * Keep what that expansion did not print: a title, and a description that a reference sibling replaced.
 */
const renderShownModel = async (name, view, previous, description) => {
    const label = view.title && view.title !== name
        ? [strong(text(view.title)), text(' ('), inlineCode(name), text(')')]
        : [inlineCode(name)];
    label.push(text(' — shown above'));
    if (previous.name !== name) {
        label.push(text(' as '), inlineCode(previous.name));
    }
    label.push(text('.'));
    const blocks = [paragraph(...label)];
    if (view.description && view.description !== previous.description) {
        blocks.push(...(await description(view.description)));
    }
    return item(...blocks);
};
/** Build Markdown directly, retaining caches only for this immutable document snapshot. */
export const createDocumentRenderer = () => {
    const descriptions = createDescriptionParser();
    const schemaRenderer = createSchemaRenderer();
    return async (document, options = {}) => {
        const description = descriptions();
        const whole = [options.operation, options.webhook, options.model, options.tag, options.introduction].every((selector) => selector === undefined);
        const anchors = createDocumentAnchors();
        const examples = whole ? createDocumentExamples(anchors) : undefined;
        const documentContext = whole
            ? {
                anchors,
                servers: document.servers !== undefined ? anchors.get('context', 'global-servers') : undefined,
                authentication: document.security !== undefined ? anchors.get('context', 'global-authentication') : undefined,
                pathServers: new WeakMap(),
            }
            : undefined;
        const destinations = whole
            ? new Map(Object.keys(document.components?.schemas ?? {}).map((name) => [
                name,
                `#${encodeURIComponent(anchors.get('schema', name))}`,
            ]))
            : undefined;
        const groups = [
            { title: 'Operations', paths: document.paths, webhook: false },
            { title: 'Webhooks', paths: document.webhooks, webhook: true },
        ].map((group) => {
            const entries = [];
            for (const [path, reference] of Object.entries(group.paths ?? {})) {
                const pathItem = getResolvedPathItem(reference);
                if (!pathItem) {
                    continue;
                }
                forEachPathItemOperation(reference, (method, operation) => {
                    const label = `${formatOperationMethod(method)} ${path}`;
                    entries.push({
                        path,
                        method,
                        pathItem,
                        operation: getResolvedRef(operation, mergeSiblingReferences),
                        label,
                        id: whole
                            ? anchors.get(group.webhook ? 'webhook' : 'operation', JSON.stringify([path, method]), label)
                            : undefined,
                    });
                });
            }
            return { ...group, entries };
        });
        // Each page expands a shared schema once, then refers back to it.
        const schemas = schemaRenderer.forDocument(document.components?.schemas, options, destinations);
        const openapiVersion = document['x-original-oas-version'] ?? document.openapi;
        // A page for one operation, webhook or model starts with that item, not with the API.
        const single = options.operation !== undefined || options.webhook !== undefined || options.model !== undefined;
        const nodes = [];
        const sections = [];
        const flush = async () => {
            if (!nodes.length) {
                return;
            }
            const children = nodes.splice(0);
            await expandDescriptions(children, description);
            sections.push(serializer.stringify({ type: 'root', children }).trimEnd());
        };
        if (!single) {
            const { info } = document;
            const metadata = [field('OpenAPI Version', inlineCode(document.openapi))];
            if (info.version) {
                metadata.push(field('API Version', inlineCode(info.version)));
            }
            if (info.termsOfService) {
                metadata.push(field('Terms of service', link(info.termsOfService, info.termsOfService)));
            }
            if (info.contact) {
                const contact = [text(info.contact.name ?? '')];
                metadata.push(item(paragraph(strong(text('Contact:')), text(' '), ...contact, ...(info.contact.url ? [text(' '), link(info.contact.url, info.contact.url)] : []), ...(info.contact.email ? [text(' '), link(`mailto:${info.contact.email}`, info.contact.email)] : []))));
            }
            if (info.license) {
                metadata.push(field('License', info.license.url ? link(info.license.url, info.license.name ?? '') : text(info.license.name)));
            }
            nodes.push(heading(1, text(info.title)), list(metadata), ...(await description(info.description)));
            if (whole) {
                const contents = [];
                for (const group of groups) {
                    if (!group.entries.length) {
                        continue;
                    }
                    contents.push(paragraph(strong(text(group.title))), list(group.entries.map(({ id, label }) => item(paragraph(link(`#${encodeURIComponent(id)}`, label))))));
                }
                if (destinations?.size) {
                    contents.push(paragraph(strong(text('Schemas'))), list([...destinations].map(([name, url]) => item(paragraph(link(url, name))))));
                }
                if (contents.length) {
                    nodes.push(heading(2, text('Contents')), ...contents);
                }
            }
            if (document.servers?.length || (whole && document.servers !== undefined)) {
                if (documentContext?.servers) {
                    nodes.push(anchor(documentContext.servers));
                }
                nodes.push(heading(2, text('Servers')));
                const effectiveServers = document.servers?.length
                    ? document.servers
                    : [{ url: '/' }];
                const servers = effectiveServers.map((server) => {
                    const nested = [];
                    if (server.description) {
                        nested.push(field('Description', text(server.description)));
                    }
                    const variables = Object.entries(server.variables ?? {});
                    if (variables.length) {
                        nested.push(item(paragraph(strong(text('Variables:'))), list(variables.map(([name, variable]) => item(paragraph(inlineCode(name), text(' (default: '), inlineCode(variable.default), text(')'), ...(whole && variable.enum?.length
                            ? [text(', possible values: '), inlineCode(variable.enum.join(', '))]
                            : []), text(variable.description ? `: ${variable.description}` : '')))))));
                    }
                    const entry = field('URL', inlineCode(server.url));
                    if (nested.length) {
                        entry.children.push(list(nested));
                    }
                    return entry;
                });
                nodes.push(list(servers));
            }
            if (documentContext?.authentication) {
                nodes.push(anchor(documentContext.authentication));
            }
            nodes.push(...(await renderSecurity(document.security, document.components?.securitySchemes, description, 2)));
            if (document.tags?.length) {
                nodes.push(heading(2, text('Tags')));
                for (const tag of document.tags) {
                    const title = /^3\.2\./.test(openapiVersion) ? (tag.summary ?? tag.name) : tag.name;
                    nodes.push(heading(3, text(title)), ...(await description(tag.description)));
                    if (tag.externalDocs) {
                        nodes.push(paragraph(link(tag.externalDocs.url, tag.externalDocs.description ?? tag.externalDocs.url)));
                    }
                }
            }
            await flush();
        }
        for (const group of groups) {
            let hasOperations = false;
            for (const { path, pathItem, method, operation, id } of group.entries) {
                if (!hasOperations && !single) {
                    nodes.push(heading(2, text(group.title)));
                }
                hasOperations = true;
                schemas.beginSection();
                if (id) {
                    nodes.push(anchor(id));
                }
                nodes.push(...(await renderOperation(document, path, method, pathItem, operation, group.webhook, {
                    description,
                    schemas,
                    examples,
                    documentContext,
                    level: single ? 1 : 3,
                })));
                await flush();
            }
        }
        // A model page renders its own model first, as the page title.
        const models = Object.entries(document.components?.schemas ?? {}).sort(([a], [b]) => Number(b === options.model) - Number(a === options.model));
        const renderModel = async (name, schema, level) => {
            const view = schemas.view(schema);
            const summary = schemas.summarize(schema);
            if (whole) {
                nodes.push(anchor(anchors.get('schema', name)));
            }
            nodes.push(heading(level, text(view.title ?? name)));
            if (summary.length) {
                nodes.push(paragraph(strong(text('Type:')), text('\u00a0'), ...summary));
            }
            nodes.push(...(await description(view.description)), ...schemas.render(schema, 0, [], { hideDetails: true, name }));
            if (view.type === 'object' || hasAuthoredExamples(view)) {
                // A model page's own schema is bounded, so it gets a generated example even in linked mode.
                const own = name === options.model;
                nodes.push(...(await renderExamples({ schema: own ? schemas.exampleSchema(schema) : schema }, description, 'application/json', undefined, openapiVersion, document.openapi, {
                    linked: schemas.linked && !own,
                    quiet: schemas.linked,
                    examples,
                })));
            }
            await flush();
        };
        const selected = models.find(([name]) => name === options.model);
        if (selected) {
            schemas.beginSection();
            await renderModel(selected[0], selected[1], 1);
        }
        const dependencies = models.filter(([name]) => name !== options.model);
        if (dependencies.length) {
            nodes.push(heading(2, text('Schemas')));
        }
        // Models the page already expanded cost one line each, grouped into a single list.
        const shownAbove = [];
        const flushShownAbove = () => {
            if (shownAbove.length) {
                nodes.push(list(shownAbove.splice(0)));
            }
        };
        for (const [name, schema] of dependencies) {
            schemas.beginSection();
            const view = schemas.view(schema);
            const previous = schemas.shownAs(schema);
            // A generated example only restates the schema, but an authored one is not printed above.
            if (!whole && previous && !hasAuthoredExamples(view)) {
                shownAbove.push(await renderShownModel(name, view, previous, description));
                continue;
            }
            flushShownAbove();
            await renderModel(name, schema, 3);
        }
        flushShownAbove();
        await flush();
        return `${sections.join('\n\n')}\n`;
    };
};
